/**
 * ForgotPassword Screen
 * 
 * Allows users to initiate password reset flow
 * - Validates email or mobile number
 * - Sends OTP to provided contact
 * - Navigates to OTP screen on success
 * - Stores OTP info in Redux for verification
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 */
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Text, TextInput } from 'react-native-paper';
import { Formik } from 'formik/dist';
import * as Yup from 'yup';
import { theme } from '../theme/theme';
import { hideLoader, setOTPInfo, showLoader } from '../store/info.slice';
import { Email_RegExp, errorHandler, forgotPassword, Mobile_RegExp } from '../services/Utils';
import { useDispatch, useSelector } from 'react-redux';
import AggrLogo from '../components/AggrLogo';
import RecaptchaV2 from '../components/RecaptchaV2';
import RecaptchaNotice from '../components/RecaptchaNotice';
import { getRecaptchaChallenge, getRecaptchaHeaders } from '../services/Recaptcha';

// Constants for form input styles
const InputRoundness = 32;


function ForgotPassword({ navigation }) {
    const dispatch = useDispatch();
    const info = useSelector((state) => state.info?.info);
    const [captchaChallenge, setCaptchaChallenge] = React.useState(null);
    const [captchaV2Token, setCaptchaV2Token] = React.useState('');

    const scheme = {
        mobile: Yup.string()
            .required('Email or mobile number is required')
            .test(
                'is-email-or-mobile',
                'Enter a valid email or mobile number',
                value => {
                    return Email_RegExp.test(value) || Mobile_RegExp.test(value);
                }
            ),
    };
    const initialValues = { mobile: '' };
    const ForgotPasswordSchema = Yup.object().shape(scheme);

    /**
     * Handles forgot password form submission
     * Sends reset request and navigates to OTP screen
     * @param {Object} params - Formik params
     * @param {Object} params.values - Form values (mobile/email)
     * @param {Function} params.resetForm - Formik reset function
     * @param {Function} params.setSubmitting - Formik setSubmitting function
     */
    const onSubmitHandler = async ({ values, resetForm, setSubmitting }) => {
        if (captchaChallenge && !captchaV2Token) { return; }
        dispatch(showLoader());
        let params = {
            ...values,
        };
        const headers = await getRecaptchaHeaders(info?.recaptcha, 'cash_counter_forgot_password', captchaV2Token);
        forgotPassword(params, { headers }).then(response => {
            resetForm();
            dispatch(hideLoader());
            dispatch(setOTPInfo({ ...values, action: 'forgot-password', token: response.token, primary_identifier: response.primary_identifier }));
            navigation.push('OTP');
        }).catch(error => {
            dispatch(hideLoader());
            const challenge = getRecaptchaChallenge(error, info?.recaptcha);
            if (challenge) {
                setCaptchaChallenge(challenge);
                setCaptchaV2Token('');
                setSubmitting(false);
                return;
            }
            errorHandler(error);
            setSubmitting(false);
        });
    };

    return (
        <SafeAreaView edges={['bottom']} style={[styles.container, { backgroundColor: theme.colors.primary }]}>
            <View style={styles.mainContainer}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={ForgotPasswordSchema}
                    onSubmit={(values, { resetForm, setSubmitting }) => {
                        console.log('Form submitted with:', values);
                        onSubmitHandler({ values, resetForm, setSubmitting });
                    }}
                >
                    {({ handleChange, handleBlur, handleSubmit, isSubmitting, values, errors, touched }) => (
                        <ScrollView contentContainerStyle={styles.KeyboardAwareScrollView}>
                            <AggrLogo />
                            <View style={styles.inputContainer}>
                                <Text variant="titleMedium">{'Mobile/Email'}</Text>
                                <TextInput
                                    placeholder={'Enter mobile number or email'}
                                    mode="outlined"
                                    theme={{ roundness: InputRoundness }}
                                    style={styles.input}
                                    contentStyle={styles.contentStyle}
                                    keyboardType={'default'}
                                    value={values.mobile}
                                    onChangeText={handleChange('mobile')}
                                    onBlur={handleBlur('mobile')}
                                />
                                {touched.mobile && errors.mobile && (
                                    <Text style={styles.errorText}>{errors.mobile}</Text>
                                )}
                            </View>

                            {captchaChallenge?.siteKey ? (
                                <RecaptchaV2
                                    key={`${captchaChallenge.code}-${captchaChallenge.siteKey}-${captchaChallenge.attempt}`}
                                    siteKey={captchaChallenge.siteKey}
                                    onToken={setCaptchaV2Token}
                                />
                            ) : null}

                            <View style={styles.inputContainer}>
                                <Button
                                    theme={{ roundness: 36 }}
                                    onPress={handleSubmit}
                                    mode="contained"
                                    labelStyle={styles.labelStyle}
                                    disabled={isSubmitting || (captchaChallenge && !captchaV2Token)}
                                >
                                    Send Code
                                </Button>
                            </View>
                            <RecaptchaNotice visible={info?.recaptcha?.enabled} />
                        </ScrollView>
                    )}
                </Formik>
            </View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    mainContainer: {
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 16,
        margin: 8,
    },
    KeyboardAwareScrollView: {
        flexGrow: 1,
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 8,
    },
    input: {
        fontSize: 14,
        backgroundColor: '#f6f8fa',
        marginVertical: 4,
    },
    contentStyle: {
        paddingLeft: 16,
    },
    inputContainer: {
        marginVertical: 8,
    },
    labelStyle: {
        fontSize: 16,
        letterSpacing: 2,
        paddingVertical: 4,
    },
    errorText: {
        color: 'red',
        fontSize: 12,
        marginTop: 4,
    },
});

export default ForgotPassword;
